import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AppShell from "@/components/AppShell";
import { api } from "@/lib/api";
import { motion, AnimatePresence } from "framer-motion";
import { BookmarkSimple, Sparkle, X, Heart, ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function LookBook() {
  const [outfits, setOutfits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/outfits", { params: { saved_only: true } });
      setOutfits(data);
    } catch (e) { /* ignore */ }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const unsave = async (id) => {
    // Optimistic: remove from list
    const prev = outfits;
    setOutfits((p) => p.filter((o) => o.id !== id));
    setSelected(null);
    try {
      await api.post(`/outfits/${id}/unsave`);
      toast.success("Removed from look book");
    } catch {
      setOutfits(prev); // rollback
      toast.error("Could not update");
    }
  };

  return (
    <AppShell>
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6">
        <div>
          <p className="text-[0.7rem] tracking-[0.3em] uppercase text-charcoal-500">Your look book</p>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl mt-3 text-charcoal-900 tracking-tight">
            Saved <em className="italic">looks</em>.
          </h1>
          <p className="mt-3 text-charcoal-600 max-w-xl">
            Every outfit you bookmarked — a private little moodboard of your best self.
          </p>
        </div>
        <Link to="/occasion" data-testid="generate-new-look" className="btn-primary-pill">
          <Sparkle size={16} weight="fill" className="mr-2" /> Generate a new look
        </Link>
      </div>

      {loading ? (
        <div className="mt-16 text-center text-charcoal-500 font-serif italic text-2xl">
          Pulling your favorites…
        </div>
      ) : outfits.length === 0 ? (
        <EmptyState />
      ) : (
        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <AnimatePresence>
            {outfits.map((o) => (
              <motion.button
                key={o.id}
                layout
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95 }}
                onClick={() => setSelected(o)}
                data-testid={`lookbook-card-${o.id}`}
                className="group text-left rounded-3xl border border-cream-200 bg-cream-100 p-7 flex flex-col justify-between min-h-[240px] transition-all hover:-translate-y-1 hover:shadow-md"
              >
                <div>
                  <p className="text-[0.6rem] tracking-[0.3em] uppercase text-charcoal-500">
                    {o.occasion || "Saved look"} {o.mood ? `· ${o.mood}` : ""}
                  </p>
                  <h3 className="font-serif text-2xl mt-3 text-charcoal-900 line-clamp-2">{o.title}</h3>
                  <p className="mt-3 text-sm text-charcoal-600 line-clamp-2">{o.why_it_works}</p>
                </div>
                <div className="mt-5 flex items-center justify-between">
                  <span className="tag-chip">
                    <BookmarkSimple size={12} weight="fill" className="mr-1" /> Saved
                  </span>
                  <ArrowRight size={16} className="text-charcoal-700 opacity-60 group-hover:opacity-100 group-hover:translate-x-0.5 transition-transform" />
                </div>
              </motion.button>
            ))}
          </AnimatePresence>
        </div>
      )}

      <AnimatePresence>
        {selected && (
          <OutfitDrawer outfit={selected} onClose={() => setSelected(null)} onRemove={() => unsave(selected.id)} />
        )}
      </AnimatePresence>
    </AppShell>
  );
}

function EmptyState() {
  return (
    <div className="mt-16 rounded-3xl border border-dashed border-cream-200 bg-cream-100 p-16 text-center">
      <BookmarkSimple size={36} weight="thin" className="mx-auto text-charcoal-700" />
      <h3 className="font-serif text-3xl mt-4">Your look book is waiting.</h3>
      <p className="text-charcoal-600 mt-3 max-w-md mx-auto">
        Generate an outfit in Occasion Mode, hit <em>Save to look book</em>, and it will live here.
      </p>
      <Link to="/occasion" data-testid="empty-generate-cta" className="btn-primary-pill mt-8 inline-flex">
        <Sparkle size={16} weight="fill" className="mr-2" /> Create my first look
      </Link>
    </div>
  );
}

function OutfitDrawer({ outfit, onClose, onRemove }) {
  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-charcoal-900/40 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-6"
      onClick={onClose}
    >
      <motion.div
        initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }}
        transition={{ duration: 0.3 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-cream-50 w-full max-w-3xl rounded-t-3xl sm:rounded-3xl max-h-[92vh] overflow-y-auto border border-cream-200"
        data-testid="lookbook-drawer"
      >
        <div className="p-7 sm:p-10">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-[0.65rem] tracking-[0.3em] uppercase text-charcoal-500">
                {outfit.occasion || "Saved look"} {outfit.mood ? `· ${outfit.mood}` : ""} {outfit.weather ? `· ${outfit.weather}` : ""}
              </p>
              <h2 className="font-serif text-3xl sm:text-4xl mt-3 text-charcoal-900 tracking-tight">{outfit.title}</h2>
            </div>
            <button onClick={onClose} data-testid="drawer-close" className="rounded-full p-2 border border-cream-200 hover:bg-cream-100">
              <X size={16} />
            </button>
          </div>

          <div className="mt-8 grid sm:grid-cols-2 gap-x-10 gap-y-5">
            <Line label="Top" value={outfit.top} />
            <Line label="Bottom" value={outfit.bottom} />
            <Line label="Shoes" value={outfit.shoes} />
            <Line label="Accessories" value={outfit.accessories} />
            <Line label="Hair" value={outfit.hair} />
            <Line label="Makeup" value={outfit.makeup} />
          </div>

          {outfit.why_it_works && (
            <div className="mt-8 rounded-2xl border border-cream-200 bg-accent-butter p-5">
              <p className="text-[0.65rem] tracking-[0.3em] uppercase text-charcoal-500 mb-2">Why it works</p>
              <p className="text-charcoal-800 leading-relaxed">{outfit.why_it_works}</p>
            </div>
          )}

          <div className="mt-5 grid sm:grid-cols-2 gap-4">
            {outfit.bolder_variation && (
              <div className="rounded-2xl border border-cream-200 bg-accent-sage p-5">
                <p className="text-[0.65rem] tracking-[0.3em] uppercase text-charcoal-500 mb-2">Bolder</p>
                <p className="text-sm text-charcoal-800">{outfit.bolder_variation}</p>
              </div>
            )}
            {outfit.softer_variation && (
              <div className="rounded-2xl border border-cream-200 bg-accent-lavender p-5">
                <p className="text-[0.65rem] tracking-[0.3em] uppercase text-charcoal-500 mb-2">Softer</p>
                <p className="text-sm text-charcoal-800">{outfit.softer_variation}</p>
              </div>
            )}
          </div>

          {outfit.confidence_note && (
            <div className="mt-5 flex items-start gap-3 rounded-2xl border border-cream-200 bg-cream-100 p-5">
              <Heart size={22} weight="thin" className="flex-shrink-0 mt-0.5" />
              <p className="font-serif italic text-xl text-charcoal-800">"{outfit.confidence_note}"</p>
            </div>
          )}

          <div className="mt-8 flex items-center justify-between">
            <button onClick={onRemove} data-testid="remove-from-lookbook" className="btn-outline-pill">
              Remove from look book
            </button>
            <Link to="/occasion" onClick={onClose} data-testid="drawer-new-look" className="btn-primary-pill">
              <Sparkle size={16} weight="fill" className="mr-2" /> Create another
            </Link>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

function Line({ label, value }) {
  return (
    <div>
      <p className="text-[0.6rem] tracking-[0.3em] uppercase text-charcoal-500">{label}</p>
      <p className="mt-1 text-charcoal-800 leading-relaxed">{value || "—"}</p>
    </div>
  );
}
